import type {
  AgentBuilderHistory,
  AgentBuilderResponse,
  AgentGraphOutputs,
  AgentReleaseContext,
  AgentTool,
  AgentVersionInfo,
  SkillFileNode,
} from '@onefootprint/request-types/dashboard';
import isEqual from 'lodash/isEqual';
import type { InstructionBlockState } from './instruction-validation';
import { getAtPath } from './skills-path';
import type { Bundle, BundleNode, DecisionNode, InputField, RulesNode, SessionMeta, Task, Validations } from './types';
import { EMPTY_HISTORY, INPUT_NODE_ID, isDecisionNode, isManagedNode, isRulesNode, isTaskNode } from './types';
import { agentInputToWire, bundleFromResponse, sessionFromResponse } from './wire';

export type State = {
  instructionBlocks: Record<string, InstructionBlockState>;
  showInstructionErrors: boolean;
  instructionFocusRequest: { target: string } | null;
  bundle: Bundle;
  savedBundle: Bundle | null;
  scrollTarget: string | null;
  highlightedId: string | null;
  focusedSuggestionId: string | null;
  focusNodeId: string | null;
  /** A report section made outside its builder, focused when the builder mounts. Cleared once the author leaves it, not by a save's response, which can land before that mount. */
  focusSectionId: string | null;
  session: SessionMeta | null;
  validations: Validations;
  history: AgentBuilderHistory;
  latestRelease: AgentReleaseContext | null;
  selectedSkillPath: string | null;
  current: AgentVersionInfo | null;
  canIncludeAllVaultDocuments: boolean;
  canAddDecisionNodes: boolean;
};

export type Action =
  | { type: 'instructionBlock'; id: string; block: InstructionBlockState | null }
  | { type: 'revealInstructionErrors'; target?: string }
  | { type: 'instructionFocused' }
  | { type: 'scrollTo'; id: string | null }
  | { type: 'setHighlight'; id: string | null }
  | { type: 'setFocusedSuggestion'; id: string | null }
  | { type: 'focusSection'; id: string | null }
  | { type: 'updateTask'; taskId: string; updater: (t: Task) => Task }
  | { type: 'updateRulesNode'; nodeId: string; updater: (r: RulesNode) => RulesNode }
  | { type: 'updateDecisionNode'; nodeId: string; updater: (d: DecisionNode) => DecisionNode }
  | { type: 'updateManagedNode'; id: string; config: Record<string, unknown> }
  | { type: 'updateInputs'; inputs: InputField[] }
  | { type: 'updateVaultDocumentAccess'; enabled: boolean }
  | { type: 'updateNorms'; norms: string }
  | { type: 'updateSkills'; skills: SkillFileNode[] }
  | { type: 'updateTools'; tools: AgentTool[] }
  | { type: 'updateOutputs'; outputs: AgentGraphOutputs }
  | { type: 'setSelectedSkillPath'; path: string | null }
  | { type: 'bootstrapFromApi'; response: AgentBuilderResponse }
  | { type: 'applyBackendResponse'; response: AgentBuilderResponse };

export const initialState: State = {
  instructionBlocks: {},
  showInstructionErrors: false,
  instructionFocusRequest: null,
  bundle: {
    name: 'Untitled agent',
    norms: '',
    inputs: [],
    nodes: [],
    skills: [],
    tools: [],
    suggestions: [],
  },
  savedBundle: null,
  scrollTarget: null,
  highlightedId: null,
  focusedSuggestionId: null,
  focusNodeId: null,
  focusSectionId: null,
  session: null,
  validations: [],
  history: EMPTY_HISTORY,
  latestRelease: null,
  selectedSkillPath: null,
  current: null,
  canIncludeAllVaultDocuments: false,
  canAddDecisionNodes: false,
};

export const reducer = (state: State, action: Action): State => {
  if (action.type === 'instructionFocused') return { ...state, instructionFocusRequest: null };
  if (action.type === 'instructionBlock') {
    const instructionBlocks = { ...state.instructionBlocks };
    if (action.block) instructionBlocks[action.id] = action.block;
    else delete instructionBlocks[action.id];
    return { ...state, instructionBlocks };
  }
  if (action.type === 'revealInstructionErrors') {
    return {
      ...state,
      showInstructionErrors: true,
      instructionFocusRequest: action.target ? { target: action.target } : null,
    };
  }
  if (action.type === 'bootstrapFromApi') {
    const { response } = action;
    const bundle = bundleFromResponse(response);
    const session = sessionFromResponse(response);
    return {
      ...state,
      instructionBlocks: {},
      showInstructionErrors: false,
      instructionFocusRequest: null,
      bundle,
      savedBundle: bundle,
      session,
      validations: filterValidationsForBundle(response.validations ?? [], bundle),
      history: response.history,
      latestRelease: response.latestRelease ?? null,
      current: response.current,
      canIncludeAllVaultDocuments: response.canIncludeAllVaultDocuments,
      canAddDecisionNodes: response.canAddDecisionNodes,
    };
  }

  if (action.type === 'applyBackendResponse') {
    const { response } = action;
    const responseBundle = bundleFromResponse(response);
    // Preserve any locally-dirty slice over the BE version so a cross-surface PATCH
    // response (e.g. a norms PATCH landing while the user is mid-edit on a task)
    // doesn't clobber in-progress work. Dirty = differs from the last savedBundle.
    const bundle = mergeDirtySlices(state.bundle, state.savedBundle, responseBundle);
    const selectedSkillPath =
      state.selectedSkillPath && getAtPath(bundle.skills, state.selectedSkillPath) ? state.selectedSkillPath : null;
    const enteringPreview = !state.current?.isHistoricalPreview && response.current.isHistoricalPreview;
    // Drop the focused id if its suggestion is no longer in the new bundle (BE just applied or rejected it).
    const focusedSuggestionId =
      state.focusedSuggestionId && bundle.suggestions.some(s => s.id === state.focusedSuggestionId)
        ? state.focusedSuggestionId
        : null;
    return {
      ...state,
      bundle,
      savedBundle: responseBundle,
      highlightedId: null,
      focusedSuggestionId,
      focusNodeId: response.operationResult?.kind === 'add_node' ? response.operationResult.nodeId : null,
      scrollTarget: enteringPreview ? null : state.scrollTarget,
      validations: filterValidationsForBundle(response.validations ?? [], bundle),
      history: response.history,
      session: sessionFromResponse(response),
      latestRelease: response.latestRelease ?? null,
      selectedSkillPath,
      current: response.current,
      canIncludeAllVaultDocuments: response.canIncludeAllVaultDocuments,
      canAddDecisionNodes: response.canAddDecisionNodes,
    };
  }

  if (action.type === 'scrollTo') {
    return { ...state, scrollTarget: action.id };
  }

  if (action.type === 'setHighlight') {
    return { ...state, highlightedId: action.id };
  }

  if (action.type === 'focusSection') {
    return { ...state, focusSectionId: action.id };
  }

  if (action.type === 'setFocusedSuggestion') {
    return { ...state, focusedSuggestionId: action.id };
  }

  if (action.type === 'updateTask') {
    return {
      ...state,
      bundle: {
        ...state.bundle,
        nodes: state.bundle.nodes.map(n => (isTaskNode(n) && n.id === action.taskId ? action.updater(n) : n)),
      },
    };
  }

  if (action.type === 'updateRulesNode') {
    return {
      ...state,
      bundle: {
        ...state.bundle,
        nodes: state.bundle.nodes.map(n => (isRulesNode(n) && n.id === action.nodeId ? action.updater(n) : n)),
      },
    };
  }

  if (action.type === 'updateDecisionNode') {
    return {
      ...state,
      bundle: {
        ...state.bundle,
        nodes: state.bundle.nodes.map(n => (isDecisionNode(n) && n.id === action.nodeId ? action.updater(n) : n)),
      },
    };
  }

  if (action.type === 'updateManagedNode') {
    return {
      ...state,
      bundle: {
        ...state.bundle,
        nodes: state.bundle.nodes.map(n =>
          isManagedNode(n) && n.id === action.id ? { ...n, config: action.config } : n,
        ),
      },
    };
  }

  if (action.type === 'updateInputs') {
    return {
      ...state,
      bundle: { ...state.bundle, inputs: action.inputs },
    };
  }

  if (action.type === 'updateVaultDocumentAccess') {
    return { ...state, bundle: { ...state.bundle, includeAllVaultDocuments: action.enabled } };
  }

  if (action.type === 'updateNorms') {
    return {
      ...state,
      bundle: { ...state.bundle, norms: action.norms },
    };
  }

  if (action.type === 'updateSkills') {
    const skills = action.skills;
    const selectedSkillPath =
      state.selectedSkillPath && getAtPath(skills, state.selectedSkillPath) ? state.selectedSkillPath : null;
    return { ...state, bundle: { ...state.bundle, skills }, selectedSkillPath };
  }

  if (action.type === 'updateTools') {
    return {
      ...state,
      bundle: { ...state.bundle, tools: action.tools },
    };
  }

  if (action.type === 'updateOutputs') {
    return {
      ...state,
      bundle: { ...state.bundle, outputs: action.outputs },
    };
  }

  if (action.type === 'setSelectedSkillPath') {
    return { ...state, selectedSkillPath: action.path };
  }

  return state;
};

const getValidationNodeIds = (bundle: Bundle): Set<string> => {
  return new Set<string>([...bundle.nodes.map(n => n.id), INPUT_NODE_ID]);
};

export const filterValidationsForBundle = (validations: Validations, bundle: Bundle): Validations => {
  const liveNodeIds = getValidationNodeIds(bundle);
  return validations.filter(v => v.id === undefined || liveNodeIds.has(v.id));
};

// InputField.id is a client-only key regenerated on every parse, so we compare
// inputs on the wire shape (fieldName/comment) — including the id would make
// every roundtrip look dirty and re-PATCH on the next blur.
const inputsEqual = (a: InputField[], b: InputField[]): boolean => {
  return isEqual(a.map(agentInputToWire), b.map(agentInputToWire));
};

// Field, artifact and property ids are client-only in the same way, and the wire rebuilds each one
// from its key — so a node echoed back from its own save carries different child ids than the ones
// sent, and a raw comparison calls it dirty forever: every blur re-PATCHes it, minting an agent
// version, and undo re-applies the stale local copy over the version it just restored.
//
// Comparing the two WIRE forms would also fix that, but the wire drops state the editor is still
// holding — a property row that has no name yet, enum values kept across a type change — and a node
// that compares equal is the node this merge throws away. So drop only the ids. The JSON round trip
// is what makes absent and `undefined` compare alike, which they must: a locally built field omits
// `enumValues` where one read back from the wire carries it as `undefined`.
const withoutIds = (node: BundleNode): unknown =>
  JSON.parse(JSON.stringify(node, (key, value) => (key === 'id' ? undefined : value)));

// `mergeDirtySlices` keeps an unsaved slice over the response, so an operation's change to it is lost.
export const hasUnsavedInputs = (state: State): boolean =>
  !!state.savedBundle && !inputsEqual(state.bundle.inputs, state.savedBundle.inputs);

export const hasUnsavedTools = (state: State): boolean =>
  !!state.savedBundle && !isEqual(state.bundle.tools, state.savedBundle.tools);

export const nodesEqual = (a: BundleNode, b: BundleNode | undefined): boolean =>
  b !== undefined && isEqual(withoutIds(a), withoutIds(b));

const mergeDirtySlices = (local: Bundle, saved: Bundle | null, server: Bundle): Bundle => {
  if (!saved) return server;
  const savedById = new Map(saved.nodes.map(node => [node.id, node]));
  const dirtyById = new Map(
    local.nodes.filter(node => !nodesEqual(node, savedById.get(node.id))).map(node => [node.id, node]),
  );
  return {
    ...server,
    // A dirty local node only wins while it is still the same kind of node. `make_managed_node_custom`
    // swaps a managed node for a task under the same id, and a stale local copy would undo it.
    nodes: server.nodes.map(node => {
      const dirty = dirtyById.get(node.id);
      return dirty?.kind === node.kind ? dirty : node;
    }),
    norms: local.norms === saved.norms ? server.norms : local.norms,
    inputs: inputsEqual(local.inputs, saved.inputs) ? server.inputs : local.inputs,
    includeAllVaultDocuments:
      local.includeAllVaultDocuments === saved.includeAllVaultDocuments
        ? server.includeAllVaultDocuments
        : local.includeAllVaultDocuments,
    skills: isEqual(local.skills, saved.skills) ? server.skills : local.skills,
    tools: isEqual(local.tools, saved.tools) ? server.tools : local.tools,
    outputs: isEqual(local.outputs, saved.outputs) ? server.outputs : local.outputs,
  };
};
